import { BaseFetcher } from "./BaseFetcher";
import { Word, WordInput, WordsResponse, MultipleWordsResponse } from "../../types/words";
import { CriteriaOption } from "../../types/task";

type WordUpdateParams = {
  word: string;
  additional_info?: string;
  criteria: CriteriaOption[];
};

export default class WordsFetch extends BaseFetcher {
  async createWord(wordData: WordInput): Promise<WordsResponse> {
    this.validateWordInput(wordData);
    
    return this.apiFetcher.callApi<Word>({
      endpoint: "words",
      options: {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(wordData),
      },
    });
  }

  async createMultipleWords(wordsData: WordInput[]): Promise<MultipleWordsResponse> {
    if (!wordsData || wordsData.length === 0) {
      throw new Error("Words data is required");
    }
    
    wordsData.forEach(word => this.validateWordInput(word));
    
    return this.apiFetcher.callApi<Word[]>({
      endpoint: "words",
      options: {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(wordsData),
      },
    });
  }

  async getWord(wordKey: string): Promise<WordsResponse> {
    this.validateWordKey(wordKey);
    
    return this.apiFetcher.callApi<Word>({
      endpoint: `words/${wordKey}`,
    });
  }

  async listWords(limit = 100): Promise<MultipleWordsResponse> {
    return this.apiFetcher.callApi<Word[]>({
      endpoint: "words",
      urlParams: { limit },
    });
  }

  async updateWord(wordKey: string, params: WordUpdateParams): Promise<WordsResponse> {
    this.validateWordKey(wordKey);
    
    return this.apiFetcher.callApi<Word>({
      endpoint: `words/${wordKey}`,
      options: {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      },
    });
  }

  async deleteWord(wordKey: string): Promise<{ message: string; success: boolean }> {
    this.validateWordKey(wordKey);
    
    return this.apiFetcher.callApi<never>({
      endpoint: `words/${wordKey}`,
      options: {
        method: "DELETE",
      },
    });
  }

  private validateWordInput(wordData: WordInput): void {
    if (!wordData) {
      throw new Error("Word data is required");
    }
    if (!wordData.word || wordData.word.trim() === "") {
      throw new Error("Word is required");
    }
    if (!wordData.criteria || wordData.criteria.length === 0) {
      throw new Error("At least one criteria is required");
    }
  }

  private validateWordKey(wordKey: string): void {
    if (!wordKey || wordKey.trim() === "") {
      throw new Error("Word key is required");
    }
  }
}
